import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Users, Copy, Eye } from 'lucide-react';

interface UserStats {
  totalUsers: number;
  activeUsers7Days: number;
  activeUsers15Days: number;
  activeUsers30Days: number;
  inactiveUsers7Days: number;
  inactiveUsers15Days: number;
  inactiveUsers30Days: number;
}

interface ActivityStats {
  totalRoutines: number;
  totalMessages: number;
  totalPosts: number;
  recentRoutines: number;
  recentMessages: number;
  recentPosts: number;
  recentRoutines15Days: number;
  recentMessages15Days: number;
  recentPosts15Days: number;
  recentRoutines30Days: number;
  recentMessages30Days: number;
  recentPosts30Days: number;
  recentRoutinesUsers: number;
  recentMessagesUsers: number;
  recentPostsUsers: number;
  recentRoutinesUsers15Days: number;
  recentMessagesUsers15Days: number;
  recentPostsUsers15Days: number;
  recentRoutinesUsers30Days: number;
  recentMessagesUsers30Days: number;
  recentPostsUsers30Days: number;
}

interface User {
  id: string;
  email: string;
  nickname: string;
  createdAt: string;
  lastActivity?: string;
}

const AdminPage: React.FC = () => {
  const { isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0,
    activeUsers7Days: 0,
    activeUsers15Days: 0,
    activeUsers30Days: 0,
    inactiveUsers7Days: 0,
    inactiveUsers15Days: 0,
    inactiveUsers30Days: 0,
  });
  const [activityStats, setActivityStats] = useState<ActivityStats>({
    totalRoutines: 0,
    totalMessages: 0,
    totalPosts: 0,
    recentRoutines: 0,
    recentMessages: 0,
    recentPosts: 0,
    recentRoutines15Days: 0,
    recentMessages15Days: 0,
    recentPosts15Days: 0,
    recentRoutines30Days: 0,
    recentMessages30Days: 0,
    recentPosts30Days: 0,
    recentRoutinesUsers: 0,
    recentMessagesUsers: 0,
    recentPostsUsers: 0,
    recentRoutinesUsers15Days: 0,
    recentMessagesUsers15Days: 0,
    recentPostsUsers15Days: 0,
    recentRoutinesUsers30Days: 0,
    recentMessagesUsers30Days: 0,
    recentPostsUsers30Days: 0,
  });
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserList, setShowUserList] = useState(false);
  const [userListType, setUserListType] = useState<'7days' | '15days' | '30days'>('7days');
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [userLastActivityMap, setUserLastActivityMap] = useState<Map<string, Date>>(new Map());

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    fetchStats();
  }, [isLoggedIn, navigate]);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      
      // 사용자 통계
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const totalUsers = usersSnapshot.size;

      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const fifteenDaysAgo = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      let activeUsers7Days = 0;
      let activeUsers15Days = 0;
      let activeUsers30Days = 0;

      const usersData: User[] = [];
      // 사용자별 최근 활동 날짜를 추적하기 위한 맵
      const userLastActivityMap = new Map<string, Date>();

      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        const createdAt = new Date(userData.createdAt);
        
        // 가입일 기준으로는 활성 사용자 계산하지 않음 (실제 활동 기준으로 변경)
        usersData.push({
          id: doc.id,
          email: userData.email,
          nickname: userData.nickname,
          createdAt: userData.createdAt,
        });
      });

      // userLastActivityMap을 상태로 설정 (아직 비어있음)
      setUserLastActivityMap(userLastActivityMap);

      setUsers(usersData);

      // 루틴 통계 - 히스토리 기반으로 정확한 사용 내역 계산
      const routinesRef = collection(db, 'routines');
      const routinesSnapshot = await getDocs(routinesRef);
      const totalRoutines = routinesSnapshot.size;

      let totalRoutineHistories = 0;
      let recentRoutines7Days = 0;
      let recentRoutines15Days = 0;
      let recentRoutines30Days = 0;
      const recentRoutinesUsers = new Set();
      const recentRoutinesUsers7Days = new Set();
      const recentRoutinesUsers15Days = new Set();
      const recentRoutinesUsers30Days = new Set();

      // 각 루틴의 히스토리 서브컬렉션을 확인
      for (const routineDoc of routinesSnapshot.docs) {
        const routineData = routineDoc.data();
        const historiesRef = collection(db, 'routines', routineDoc.id, 'histories');
        const historiesSnapshot = await getDocs(historiesRef);
        
        totalRoutineHistories += historiesSnapshot.size;
        
        // 각 히스토리의 날짜를 확인하여 기간별 사용 내역 계산
        historiesSnapshot.forEach((historyDoc) => {
          const historyData = historyDoc.data();
          const historyDate = new Date(historyData.createdAt);
          const userEmail = routineData.userEmail;
          
          // 사용자별 최근 활동 날짜 업데이트
          if (!userLastActivityMap.has(userEmail) || historyDate > userLastActivityMap.get(userEmail)!) {
            userLastActivityMap.set(userEmail, historyDate);
          }
          
          if (historyDate >= sevenDaysAgo) {
            recentRoutines7Days++;
            recentRoutinesUsers7Days.add(userEmail);
            recentRoutinesUsers.add(userEmail);
          }
          if (historyDate >= fifteenDaysAgo) {
            recentRoutines15Days++;
            recentRoutinesUsers15Days.add(userEmail);
          }
          if (historyDate >= thirtyDaysAgo) {
            recentRoutines30Days++;
            recentRoutinesUsers30Days.add(userEmail);
          }
        });
      }

      // 메시지 통계 (소울) - 날짜 기반 정확한 계산
      let totalMessages = 0;
      let recentMessages7Days = 0;
      let recentMessages15Days = 0;
      let recentMessages30Days = 0;
      const recentMessagesUsers = new Set();
      const recentMessagesUsers7Days = new Set();
      const recentMessagesUsers15Days = new Set();
      const recentMessagesUsers30Days = new Set();

      for (const userDoc of usersSnapshot.docs) {
        const messagesRef = collection(db, 'users', userDoc.id, 'messages');
        const messagesSnapshot = await getDocs(messagesRef);
        totalMessages += messagesSnapshot.size;

        // 각 메시지의 timestamp를 확인하여 기간별 사용 내역 계산
        messagesSnapshot.forEach((messageDoc) => {
          const messageData = messageDoc.data();
          const messageDate = new Date(messageData.timestamp);
          const userEmail = userDoc.data().email;
          
          // 사용자별 최근 활동 날짜 업데이트
          if (!userLastActivityMap.has(userEmail) || messageDate > userLastActivityMap.get(userEmail)!) {
            userLastActivityMap.set(userEmail, messageDate);
          }
          
          if (messageDate >= sevenDaysAgo) {
            recentMessages7Days++;
            recentMessagesUsers7Days.add(userEmail);
            recentMessagesUsers.add(userEmail);
          }
          if (messageDate >= fifteenDaysAgo) {
            recentMessages15Days++;
            recentMessagesUsers15Days.add(userEmail);
          }
          if (messageDate >= thirtyDaysAgo) {
            recentMessages30Days++;
            recentMessagesUsers30Days.add(userEmail);
          }
        });
      }

      // 포스트 통계 (소세) - 날짜 기반 정확한 계산
      const postsRef = collection(db, 'posts');
      const postsSnapshot = await getDocs(postsRef);
      const totalPosts = postsSnapshot.size;

      let recentPosts7Days = 0;
      let recentPosts15Days = 0;
      let recentPosts30Days = 0;
      const recentPostsUsers = new Set();
      const recentPostsUsers7Days = new Set();
      const recentPostsUsers15Days = new Set();
      const recentPostsUsers30Days = new Set();

      postsSnapshot.forEach((doc) => {
        const postData = doc.data();
        const postDate = postData.createdAt?.toDate ? postData.createdAt.toDate() : new Date(postData.createdAt);
        const userEmail = postData.email;
        
        // 사용자별 최근 활동 날짜 업데이트
        if (!userLastActivityMap.has(userEmail) || postDate > userLastActivityMap.get(userEmail)!) {
          userLastActivityMap.set(userEmail, postDate);
        }
        
        if (postDate >= sevenDaysAgo) {
          recentPosts7Days++;
          recentPostsUsers7Days.add(userEmail);
          recentPostsUsers.add(userEmail);
        }
        if (postDate >= fifteenDaysAgo) {
          recentPosts15Days++;
          recentPostsUsers15Days.add(userEmail);
        }
        if (postDate >= thirtyDaysAgo) {
          recentPosts30Days++;
          recentPostsUsers30Days.add(userEmail);
        }
      });

      // 모든 데이터 수집이 끝난 후 활성 사용자 계산
      for (const [userEmail, lastActivityDate] of userLastActivityMap) {
        if (lastActivityDate >= sevenDaysAgo) activeUsers7Days++;
        if (lastActivityDate >= fifteenDaysAgo) activeUsers15Days++;
        if (lastActivityDate >= thirtyDaysAgo) activeUsers30Days++;
      }

      // userLastActivityMap을 최종 상태로 설정
      setUserLastActivityMap(userLastActivityMap);

      setUserStats({
        totalUsers,
        activeUsers7Days,
        activeUsers15Days,
        activeUsers30Days,
        inactiveUsers7Days: totalUsers - activeUsers7Days,
        inactiveUsers15Days: totalUsers - activeUsers15Days,
        inactiveUsers30Days: totalUsers - activeUsers30Days,
      });

      setActivityStats({
        totalRoutines: totalRoutineHistories, // 히스토리 총 개수로 변경
        totalMessages,
        totalPosts,
        recentRoutines: recentRoutines7Days, // 7일간 루틴 사용 내역
        recentMessages: recentMessages7Days, // 7일간 메시지 사용 내역
        recentPosts: recentPosts7Days, // 7일간 포스트 사용 내역
        recentRoutines15Days: recentRoutines15Days, // 15일간 루틴 사용 내역
        recentMessages15Days: recentMessages15Days, // 15일간 메시지 사용 내역
        recentPosts15Days: recentPosts15Days, // 15일간 포스트 사용 내역
        recentRoutines30Days: recentRoutines30Days, // 30일간 루틴 사용 내역
        recentMessages30Days: recentMessages30Days, // 30일간 메시지 사용 내역
        recentPosts30Days: recentPosts30Days, // 30일간 포스트 사용 내역
        recentRoutinesUsers: recentRoutinesUsers.size,
        recentMessagesUsers: recentMessagesUsers.size,
        recentPostsUsers: recentPostsUsers.size,
        recentRoutinesUsers15Days: recentRoutinesUsers15Days.size,
        recentMessagesUsers15Days: recentMessagesUsers15Days.size,
        recentPostsUsers15Days: recentPostsUsers15Days.size,
        recentRoutinesUsers30Days: recentRoutinesUsers30Days.size,
        recentMessagesUsers30Days: recentMessagesUsers30Days.size,
        recentPostsUsers30Days: recentPostsUsers30Days.size,
      });

    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const copyAllEmails = () => {
    const emails = users.map(user => user.email).join(', ');
    navigator.clipboard.writeText(emails);
    setShowCopyToast(true);
    setTimeout(() => setShowCopyToast(false), 2000);
  };

  const copyFilteredEmails = () => {
    const emails = getFilteredUsers(userListType).map(user => user.email).join(', ');
    navigator.clipboard.writeText(emails);
    setShowCopyToast(true);
    setTimeout(() => setShowCopyToast(false), 2000);
  };

  const getFilteredUsers = (type: '7days' | '15days' | '30days') => {
    const now = new Date();
    let daysAgo: Date;
    
    switch (type) {
      case '7days':
        daysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '15days':
        daysAgo = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        daysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
    }

    // 실제 활동 기준으로 비활성 사용자 필터링
    return users.filter(user => {
      // 사용자의 최근 활동 날짜를 찾기
      const userLastActivity = userLastActivityMap.get(user.email);
      if (!userLastActivity) {
        // 활동 기록이 없는 사용자는 비활성으로 간주
        return true;
      }
      // 마지막 활동이 지정된 기간보다 오래된 경우 비활성
      return userLastActivity < daysAgo;
    });
  };

  const showUserListModal = (type: '7days' | '15days' | '30days') => {
    setUserListType(type);
    setShowUserList(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint mx-auto"></div>
          <p className="mt-4 text-gray-600">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">        

      <div className="container mx-auto px-4 py-8">
        {/* 사용자 통계 - 3개 영역으로 분리 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* 루틴 활동 */}
          <div className="bg-indigo-50 rounded-xl shadow-sm p-6 border border-indigo-100">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
              <h2 className="text-lg font-semibold text-indigo-800">루틴</h2>
            </div>
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600">{activityStats.totalRoutines.toLocaleString()}</div>
                <div className="text-sm text-indigo-700">총 루틴</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-lg font-semibold text-indigo-600">{activityStats.recentRoutines.toLocaleString()}</div>
                  <div className="text-xs text-indigo-600">7일 이내</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-indigo-600">{activityStats.recentRoutines15Days.toLocaleString()}</div>
                  <div className="text-xs text-indigo-600">15일 이내</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-indigo-600">{activityStats.recentRoutines30Days.toLocaleString()}</div>
                  <div className="text-xs text-indigo-600">30일 이내</div>
                </div>
              </div>
            </div>
          </div>

          {/* 소울 활동 */}
          <div className="bg-purple-50 rounded-xl shadow-sm p-6 border border-purple-100">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <h2 className="text-lg font-semibold text-purple-800">쏘울</h2>
            </div>
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">{activityStats.totalMessages.toLocaleString()}</div>
                <div className="text-sm text-purple-700">총 대화</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-lg font-semibold text-purple-600">{activityStats.recentMessages.toLocaleString()}</div>
                  <div className="text-xs text-purple-600">7일 이내</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-purple-600">{activityStats.recentMessages15Days.toLocaleString()}</div>
                  <div className="text-xs text-purple-600">15일 이내</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-purple-600">{activityStats.recentMessages30Days.toLocaleString()}</div>
                  <div className="text-xs text-purple-600">30일 이내</div>
                </div>
              </div>
            </div>
          </div>

          {/* 소세 활동 */}
          <div className="bg-emerald-50 rounded-xl shadow-sm p-6 border border-emerald-100">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
              <h2 className="text-lg font-semibold text-emerald-800">쏘세</h2>
            </div>
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">{activityStats.totalPosts.toLocaleString()}</div>
                <div className="text-sm text-emerald-700">총 게시글</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-lg font-semibold text-emerald-600">{activityStats.recentPosts.toLocaleString()}</div>
                  <div className="text-xs text-emerald-600">7일 이내</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-emerald-600">{activityStats.recentPosts15Days.toLocaleString()}</div>
                  <div className="text-xs text-emerald-600">15일 이내</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-emerald-600">{activityStats.recentPosts30Days.toLocaleString()}</div>
                  <div className="text-xs text-emerald-600">30일 이내</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 비활성 사용자 영역 */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">총 사용자 {users.length}명 중 비활성 사용자</h2>
            <button
              onClick={copyAllEmails}
              className="px-4 py-2 bg-mint text-white rounded-lg hover:bg-mint/90 transition-colors flex items-center space-x-1 text-sm font-medium"
            >
              <Copy size={14} />
              <span>전체 이메일 복사</span>
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="text-2xl font-bold text-orange-600">{userStats.inactiveUsers7Days.toLocaleString()}</div>
                <div className="text-sm text-gray-600">7일 비활성</div>
              </div>
              <button
                onClick={() => showUserListModal('7days')}
                className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors flex items-center space-x-1"
              >
                <Eye size={14} />
                <span className="text-xs">목록 보기</span>
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="text-2xl font-bold text-orange-600">{userStats.inactiveUsers15Days.toLocaleString()}</div>
                <div className="text-sm text-gray-600">15일 비활성</div>
              </div>
              <button
                onClick={() => showUserListModal('15days')}
                className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors flex items-center space-x-1"
              >
                <Eye size={14} />
                <span className="text-xs">목록 보기</span>
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="text-2xl font-bold text-red-600">{userStats.inactiveUsers30Days.toLocaleString()}</div>
                <div className="text-sm text-gray-600">30일 비활성</div>
              </div>
              <button
                onClick={() => showUserListModal('30days')}
                className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center space-x-1"
              >
                <Eye size={14} />
                <span className="text-xs">목록 보기</span>
              </button>
            </div>
          </div>
        </div>

        {/* 최근 7일 활동 */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">최근 7일 활동</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600">{activityStats.recentRoutines.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mb-2">루틴</div>
              <div className="text-xs text-indigo-500">{activityStats.recentRoutinesUsers}명의 사용자</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{activityStats.recentMessages.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mb-2">쏘울</div>
              <div className="text-xs text-purple-500">{activityStats.recentMessagesUsers}명의 사용자</div>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600">{activityStats.recentPosts.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mb-2">쏘쎄</div>
              <div className="text-xs text-emerald-500">{activityStats.recentPostsUsers}명의 사용자</div>
            </div>
          </div>
        </div>

        {/* 최근 15일 활동 */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">최근 15일 활동</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600">{activityStats.recentRoutines15Days.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mb-2">루틴</div>
              <div className="text-xs text-indigo-500">{activityStats.recentRoutinesUsers15Days}명의 사용자</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{activityStats.recentMessages15Days.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mb-2">쏘울</div>
              <div className="text-xs text-purple-500">{activityStats.recentMessagesUsers15Days}명의 사용자</div>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600">{activityStats.recentPosts15Days.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mb-2">쏘쎄</div>
              <div className="text-xs text-emerald-500">{activityStats.recentPostsUsers15Days}명의 사용자</div>
            </div>
          </div>
        </div>

        {/* 최근 30일 활동 */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">최근 30일 활동</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600">{activityStats.recentRoutines30Days.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mb-2">루틴</div>
              <div className="text-xs text-indigo-500">{activityStats.recentRoutinesUsers30Days}명의 사용자</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{activityStats.recentMessages30Days.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mb-2">쏘울</div>
              <div className="text-xs text-purple-500">{activityStats.recentMessagesUsers30Days}명의 사용자</div>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600">{activityStats.recentPosts30Days.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mb-2">쏘쎄</div>
              <div className="text-xs text-emerald-500">{activityStats.recentPostsUsers30Days}명의 사용자</div>
            </div>
          </div>
        </div>



        {/* 새로고침 버튼 */}
        <div className="text-center">
          <button
            onClick={fetchStats}
            className="px-6 py-3 bg-mint text-white rounded-lg hover:bg-mint/90 transition-colors"
          >
            데이터 새로고침
          </button>
        </div>
      </div>

      {/* 사용자 목록 모달 */}
      {showUserList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-4xl mx-4 w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                {userListType === '7days' ? '7일' : userListType === '15days' ? '15일' : '30일'} 비활성 사용자 목록
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={copyFilteredEmails}
                  className="px-4 py-2 bg-mint text-white rounded-lg hover:bg-mint/90 transition-colors flex items-center space-x-1 text-sm font-medium"
                >
                  <Copy size={14} />
                  <span>이메일 복사</span>
                </button>
                <button
                  onClick={() => setShowUserList(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {getFilteredUsers(userListType).map((user) => (
                  <div key={user.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-900">{user.nickname}</div>
                    <div className="text-sm text-gray-600">{user.email}</div>
                    <div className="text-xs text-gray-500">
                      가입일: {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 text-center text-sm text-gray-600">
              총 {getFilteredUsers(userListType).length}명의 사용자
            </div>
          </div>
        </div>
      )}

      {/* 복사 완료 팝업 */}
      {showCopyToast && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-yellow-500 text-yellow-900 px-6 py-4 rounded-lg shadow-lg border border-yellow-600">
            <div className="flex items-center space-x-2">
              <span className="text-lg">⚠️</span>
              <span className="font-medium text-lg">복사 완료되었습니다!</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
